import { config } from '../qVariants/index';
import { VariantStatus } from '../qVariants/status';

/**
 * Medical specialty identifier.
 * Determines which knowledge base (KB) to use for the questionnaire. Each specialty is a separate universe within the neo4j database. You cannot use a question from one specialty in another.
 * @example 'MED-G' - General medicine
 * @example 'CARDIO' - Cardiology
 * @example 'SOMMEIL' - Sleep medicine
 */
export type Speciality = (typeof config.specialities)[number];

/**
 * Patient onboarding flow identifier.
 * Controls the sequence of steps in the patient app before the questionnaire begins.
 * @see apps/patient/src/onboarding/orchestration/flows.ts for flow definitions
 * @example 'MED_G' - Standard GP flow with steps-overview
 * @example 'SOMMEIL' - Sleep-specific intro pages
 * @example 'DEFAULT' - Basic onboarding without steps-overview
 */
export type PatientOnboarding = (typeof config.patientOnboardings)[number];

/**
 * Patient offboarding flow identifier.
 * Controls the sequence of steps in the patient app  after the questionnaire is completed.
 * @see apps/patient/src/onboarding/orchestration/flows.ts for flow definitions
 * @example 'DEFAULT' - research-consent → confirmation
 * @example 'DIRECT_CONFIRMATION' - Skip to direct-confirmation page
 * @example 'CONFIRMATION_ONLY' - Skip research-consent, go to confirmation
 */
export type PatientOffboarding = (typeof config.patientOffboardings)[number];

/**
 * Email template identifier.
 * Determines which email template to use for patient invitations.
 *
 * **Priority order** (first match wins):
 * 1. Practice's `customEmailTemplateId` setting (if configured)
 * 2. Variant's `emailTemplate` (this field)
 * 3. Default system template
 *
 * Most variants use 'MED_G_DEFAULT' which falls through to the system default.
 * Only set a custom value for variants that need a specific email design.
 *
 * @example 'MED_G_DEFAULT' - Falls through to system default
 * @example 'sommeil-bligny' - Custom sleep clinic template
 * @example 'mon-bilan-prevention' - Prevention checkup template
 */
export type EmailTemplate = (typeof config.emailTemplates)[number];

/**
 * Report template identifier.
 * Controls how the medical report is formatted for the practitioner within the pro app.
 * @see apps/api/src/domain/report/formatters/ for formatter implementations
 * @example 'MED_G_DEFAULT' - Full GP report with all sections
 * @example 'SLEEP' - Sleep-specific report format
 * @example 'BASIC_SPECIALTY' - Simplified 2-column report for specialists
 * @example 'QUESTIONS_ANSWERS' - Q&A format from structured data
 */
export type ReportTemplate = (typeof config.reportTemplates)[number];

/**
 * Standard runtime context handed to a {@link ReportTemplateResolver}.
 * Currently only the report's KB version; extend as new gating inputs appear.
 */
export type ReportTemplateContext = { version?: string | null };

/**
 * A variant may compute its report template from runtime context (e.g. KB
 * version) instead of declaring a static one — used when a variant's reports
 * must be rendered with different templates across KB versions.
 */
export type ReportTemplateResolver = (
  context: ReportTemplateContext,
) => ReportTemplate;

/**
 * Runtime option flag names.
 * These control questionnaire behavior and are set to `true` when listed in the variant's `options` array. Treated the same as personalization options by the questionnaire engine, but always considered "activated" when listed within the options array of the variant
 * @example 'COMPACT' - Use compact UI mode
 * @example 'ALERTES' - Enable medical alerts
 */
export type Option = (typeof config.options)[number];

/**
 * Defines a questionnaire page configuration.
 * Pages organize the questionnaire into logical steps with associated KB sections.
 */
export type Page = {
  /** Display title shown in the UI (e.g., "Consultation", "Motifs", "Antécédents") */
  title: string;
  /**
   * KB section names to include on this page.
   * Each section corresponds to a group of questions defined in the knowledge base.
   * @example ['MOTIF', 'SYMPTÔMES', 'SC_FOLLOWUP']
   */
  sections: string[];
  /**
   * Progress bar percentage values for questions in this page.
   * Each value (0-100) indicates the progress bar percentage for the question at that index.
   *
   * **How it works:**
   * - Index 0 → progress for the 1st question, index 1 → 2nd question, etc.
   * - If the patient is past the last index, the final value is used.
   * - If ALL pages have progressSteps defined, custom values are used.
   * - If ANY page omits progressSteps, automatic calculation is used for all pages.
   *
   * **Automatic calculation** (when progressSteps omitted):
   * Progress is estimated from page position + question index within the page.
   *
   * @example [1, 2, 4, 6] - Consultation page: questions progress 1% → 2% → 4% → 6%
   * @example [100] - Conclusion page: immediately shows 100%
   */
  progressSteps?: number[];
};

/**
 * Configuration for a single personalizable option.
 * Doctors can customize these flags per-practice in the pro app settings.
 */
export type PersonalizableOption = {
  /** Default value when no practice-level override is set */
  default: boolean;
  /** Estimated time impact in minutes when this option is enabled */
  duration: number;
};

/**
 * Map of personalizable option names to their configurations.
 * Keys follow naming conventions:
 * - `{CATEGORY}_ENABLED_{PATIENT_TYPE}` - Toggle entire category
 * - `{CATEGORY}_REQUIRED_{PATIENT_TYPE}` - Make category mandatory
 *
 * Where PATIENT_TYPE is either NEW_PATIENT or EXISTING_PATIENT.
 *
 * @example
 * {
 *   MOTIVES_ENABLED_NEW_PATIENT: { default: true, duration: 3 },
 *   PREVENTION_TOBACCO_REQUIRED_EXISTING_PATIENT: { default: false, duration: 0.75 }
 * }
 */
export type PersonalizableOptionsMap = Record<string, PersonalizableOption>;

/**
 * Questionnaire variant configuration.
 *
 * A variant defines a complete questionnaire experience for a specific medical context.
 * It controls the onboarding flow, question pages, report formatting, and runtime behavior.
 *
 * Variants are the primary configuration unit for the Aldebaran questionnaire system.
 * Each variant slug (the key in the Variants map) is synced to Prisma's QuestionnaireVariant enum.
 *
 * @example
 * ```typescript
 * const myVariant: Variant = {
 *   name: 'Cardiologie',
 *   description: 'Questionnaire pour les consultations de cardiologie.',
 *   speciality: 'CARDIO',
 *   patientOnboarding: 'DEFAULT',
 *   patientOffboarding: 'DEFAULT',
 *   emailTemplate: 'MED_G_DEFAULT',
 *   reportTemplate: 'BASIC_SPECIALTY',
 *   initialQuestions: [],
 *   pages: {
 *     CONSULTATION: { title: 'Consultation', sections: ['CONTEXTE'] },
 *     COMPLAINTS: { title: 'Motifs', sections: ['MOTIF', 'SYMPTÔMES'] },
 *   },
 *   initialAssertions: [
 *     '[Médecin: #z‹]-(a_spécialité)->[Cardiologie]',
 *   ],
 *   options: ['ALERTES'],
 * };
 * ```
 */
export interface Variant {
  /**
   * Human-readable display name for this variant, shown to doctors.
   * French sentence case; proper nouns keep their capitals.
   * @example 'Médecine générale'
   * @example 'Soins non programmés'
   */
  name: string;

  /**
   * Description of the variant's purpose and scope.
   * Shown to pro users on the settings page.
   * @example 'Questionnaire pour les patients de médecine générale. Couvre les motifs, les antécédents personnels et familiaux du patient ainsi que la prévention.'
   */
  description: string;

  /**
   * Medical specialty for this variant.
   * Determines which knowledge base (KB) questions are loaded.
   * Must match a specialty defined in the knowledgebase config.
   */
  speciality: Speciality;

  /**
   * Onboarding flow to use.
   * Controls which steps appear before the questionnaire begins (landing page, why-prepare, etc.).
   * @see PatientOnboarding for available values
   */
  patientOnboarding: PatientOnboarding;

  /**
   * Offboarding flow to use.
   * Controls which steps appear after the questionnaire ends (research consent, confirmation).
   * @see PatientOffboarding for available values
   */
  patientOffboarding: PatientOffboarding;

  /**
   * Email template for patient invitations.
   * Determines the email content sent when inviting patients.
   */
  emailTemplate: EmailTemplate;

  /**
   * Report template for the medical summary.
   * Controls how questionnaire responses are formatted for the doctor.
   * Either a static template or a resolver that derives it from runtime
   * context (e.g. KB version). Read it through `resolveReportTemplate`
   * (`@aldebaran/common/qVariants`) rather than accessing it directly.
   */
  reportTemplate: ReportTemplate | ReportTemplateResolver;

  /**
   * Initial question IDs to add to the questionnaire queue.
   * These are KB question IDs that will be shown in addition to the base questions.
   * Useful for starting the questionnaire with specific context-setting questions.
   * @example ['123131P'] - Add a specific screening question at start
   */
  initialQuestions: string[];

  /**
   * Page configuration map.
   * Keys are page identifiers (e.g., 'CONSULTATION', 'COMPLAINTS', 'HISTORY').
   * Values define the title and KB sections to show on each page.
   *
   * **Important**: The order of pages in the questionnaire follows this map's key order.
   * Each page groups related KB sections into a logical step of the consultation.
   */
  pages: Record<string, Page>;

  /**
   * Initial knowledge graph assertions to establish before the questionnaire starts.
   * Uses MDKG notation to set up the consultation context.
   *
   * Common patterns:
   * - `[Médecin: #z‹]-(a_spécialité)->[Specialty Name]` - Set doctor's specialty
   * - `[Médecin: #z‹]-(lieu_exercice)->[Location]` - Set practice location
   * - `[Message: #‹]-(06.a_lieu_emission)->[Lieu]-(01.DESCR)->[Salle d'Attente]...` - Set context
   *
   * @example ['[Médecin: #z‹]-(a_spécialité)->[Cardiologie]']
   */
  initialAssertions: string[];

  /**
   * Runtime option flags to enable for this variant.
   * Each option in this array is set to `true` in the questionnaire state.
   *
   * Available options:
   * - `COMPACT` - Use compact UI layout
   * - `ALERTES` - Enable medical alert display
   * - `SINGLE_MOTIVE` - Force single motive selection (no multi-select)
   * - `LIMITED_MOTIVES` - Show reduced motive list (for SNP/urgent care)
   * - `LIMITED_PREVENTION` - Reduce prevention questions (for quick visits)
   *
   * @example ['COMPACT', 'ALERTES', 'SINGLE_MOTIVE']
   */
  options: Option[];

  /**
   * Lifecycle status of this variant.
   * Drives whether the variant is offered in the Forest Admin back office and to
   * which teams. See {@link VariantStatus}.
   */
  status: VariantStatus;
  availableInDemo?: boolean;

  /**
   * Optional short tag that will be shown for the consultation within the pro app sidebar.
   * @example 'SNP' - Soins Non Programmés (unscheduled care)
   * @example 'ETP' - Éducation Thérapeutique du Patient
   */
  tag?: string;

  /**
   * Fixed visit motive to display in reports.
   * When set, this value is used as the consultation reason instead of patient-indicated motives.
   * Useful for specialized questionnaires with a single known purpose.
   * @example 'Sommeil' - Sleep consultation
   * @example 'Mon bilan prévention' - Prevention checkup
   */
  visitMotive?: string;

  /**
   * Report section name to extract motives from.
   * Overrides the default 'Motifs' section when reading consultation reasons.
   * Used when the motive information is in a different report section than the classic "Motifs".
   * @example 'Examen demandé' - For radiology/angio variants
   * @example 'Motif Principal' - For allergy variants
   */
  visitMotivesSection?: string;

  /**
   * Variant the consultation switches to when the patient continues it on
   * their own device through an invitation. The API applies the switch to
   * `Consultation.variant` when the invitation identity check succeeds, and
   * an opted-in practice sends the continuation SMS on check-in (ALDO-2759).
   * @example 'mmg' - on `mmg_borne`, the waiting-room kiosk check-in continues
   * as the full phone questionnaire
   */
  continuationVariant?: string;

  /**
   * Personalizable options that doctors can customize.
   * These appear in the pro app settings and override defaults at runtime.
   *
   * Naming convention: `{CATEGORY}_{MODIFIER}_{PATIENT_TYPE}`
   * - CATEGORY: MOTIVES, MEDICAL_HISTORY, PREVENTION_TOBACCO, SCREENING_BREAST, etc.
   * - MODIFIER: ENABLED (toggle), REQUIRED (mandatory), FREE_TEXT (allow freeform)
   * - PATIENT_TYPE: NEW_PATIENT or EXISTING_PATIENT
   *
   * @example
   * {
   *   MOTIVES_ENABLED_NEW_PATIENT: { default: true, duration: 3 },
   *   PREVENTION_TOBACCO_ENABLED_EXISTING_PATIENT: { default: true, duration: 0.2 },
   * }
   */
  personalizableOptions?: PersonalizableOptionsMap;
}

/**
 * Map of variant slug → variant config.
 *
 * Keys are free-form strings here (the source of truth for variant slugs).
 * The Prisma QuestionnaireVariant enum is generated *from* these keys
 * by `yarn prisma:sync-enum`, making @aldebaran/common the upstream
 * and the Prisma schema the downstream.
 *
 * They are used in the URL for each variant for accessing from the patient app.
 * ONCE CREATED, THE SLUG CANNOT BE CHANGED OR DELETED. Be sure to have a final slug before running a sync-enum command.
 *
 * Consumer access: `variants[key]` returns `Variant | undefined`.
 */
export type Variants = Partial<Record<string, Variant>>;

type SomeDuplicateKeys<T extends object[]> = T extends [
  infer H1 extends object,
  infer H2 extends object,
  ...infer R extends object[],
]
  ? keyof H1 & keyof H2 extends never
    ? SomeDuplicateKeys<[H1 & H2, ...R]>
    : keyof H1 & keyof H2
  : never;

type MergeAll<T extends object[]> = T extends [
  infer H extends object,
  ...infer R extends object[],
]
  ? H & MergeAll<R>
  : unknown;

type NoDuplicateKeys<T extends object[]> = SomeDuplicateKeys<T> extends infer D
  ? D extends never
    ? MergeAll<T>
    : { error: `2 Variants with same slug: ${D & string}` }
  : never;

export function mergeAll<T extends object[]>(...objs: T): NoDuplicateKeys<T> {
  return Object.assign({}, ...objs) as NoDuplicateKeys<T>;
}
